import { type Capability, roleHasCapability } from '@omecare/utils';
import { useGetMeQuery } from './queries/use-me';

/**
 * Authorization hook that provides permission and role checking methods
 * based on the current user's role and site assignments.
 *
 * This hook uses shared capability definitions from @omecare/utils to ensure
 * frontend/backend authorization logic stays in sync.
 *
 * Capability checks (hasCapability, canAccessDashboard, etc.) use the shared
 * roleHasCapability function. Site-level permission checks (canEditSites with siteId)
 * add an additional layer of filtering for CSM users.
 */
export const useAuthorization = () => {
  const { data: me, isLoading, isError, error, isFetching } = useGetMeQuery();

  // Role type checks
  const isAdmin = () => me?.role === 'admin';
  const isSupport = () => me?.role === 'support';
  const isCsm = () => me?.role === 'csm';
  const isSubstituteDoctor = () => me?.role === 'substitute_doctor';
  const isMedicalAssistant = () => me?.role === 'medical_assistant';
  const isOfficeAssistant = () => me?.role === 'office_assistant';
  const isAttendingDoctor = () => me?.role === 'attending_doctor';

  // Admin privileges check
  const hasAdminPrivileges = () => isAdmin();

  /**
   * Generic capability check using shared definition from @omecare/utils
   * This is the foundation for all capability-based checks.
   */
  const hasCapability = (capability: Capability): boolean => {
    if (!me?.role) return false;
    return roleHasCapability(me.role, capability);
  };

  // Dashboard & Analytics capabilities
  const canAccessDashboard = () => hasCapability('can_access_dashboard');

  // Accounting capabilities
  const canAccessAccounting = () => hasCapability('can_access_accounting');
  /** Gates the "Parrainage" page and the dashboard referral card. */
  const canUseReferralProgram = () => hasCapability('can_use_referral_program');
  /**
   * Gates "Nous rejoindre" — substitutes only. An attending doctor has already
   * installed, so the pitch to join Omecare does not apply to them.
   */
  const canViewRecruitmentOffer = () =>
    hasCapability('can_view_recruitment_offer');
  /** Gates the staff-facing prospect mirror. */
  const canViewProspects = () => hasCapability('can_view_prospects');
  /** Gates the administration list of shared documents and its follow-up. */
  const canViewDocuments = () => hasCapability('can_view_documents');
  /** Gates publishing a shared document. */
  const canManageDocuments = () => hasCapability('can_manage_documents');
  /** Gates the reading space of the documents shared with the user. */
  const canReadDocuments = () => hasCapability('can_read_documents');
  const canAccessAttendingAccounting = () =>
    hasCapability('can_access_attending_accounting');
  const canImportAccountingFiles = () =>
    hasCapability('can_import_accounting_files');
  const canGenerateAccounting = () => hasCapability('can_generate_accounting');

  // User management capabilities
  const canManageUsers = () => hasCapability('can_manage_users');

  // Medical assistants management capabilities (admin + support only)
  const canManageAssistants = () => hasCapability('can_manage_assistants');

  // Medical assistants planning read access (admin, support, medical_assistant)
  const canViewAssistantsPlanning = () =>
    hasCapability('can_view_assistants_planning');

  // Site capabilities
  const canViewSites = () => hasCapability('can_view_sites');
  const canEditSites = (siteId?: string) => {
    if (!hasCapability('can_edit_sites')) return false;
    // Admin and support can edit all sites
    if (isAdmin() || isSupport()) return true;
    // CSM can only edit assigned sites
    if (isCsm() && siteId) {
      return me?.siteIds?.includes(siteId) ?? false;
    }
    // CSM without siteId - has capability but needs site check
    return isCsm();
  };
  const canCreateSites = () => hasCapability('can_create_sites');
  const canDeleteSites = () => hasCapability('can_delete_sites');

  // Office capabilities
  const canViewOffices = () => hasCapability('can_view_offices');
  const canEditOffices = (siteId?: string) => {
    if (!hasCapability('can_edit_offices')) return false;
    if (isAdmin() || isSupport()) return true;
    if (isCsm() && siteId) {
      return me?.siteIds?.includes(siteId) ?? false;
    }
    return isCsm();
  };

  // Doctor capabilities
  const canViewDoctors = () => hasCapability('can_view_doctors');
  const canEditDoctors = (siteId?: string) => {
    if (!hasCapability('can_edit_doctors')) return false;
    if (isAdmin() || isSupport()) return true;
    if (isCsm() && siteId) {
      return me?.siteIds?.includes(siteId) ?? false;
    }
    return isCsm();
  };
  const canDeleteDoctors = () => hasCapability('can_delete_doctors');

  // Doctor menu access capabilities (separate from data access)
  const canAccessDoctorSubstitutesMenu = () =>
    hasCapability('can_access_doctor_substitutes_menu');
  const canAccessDoctorAttendingsMenu = () =>
    hasCapability('can_access_doctor_attendings_menu');

  // Offices menu access capability (separate from data access)
  const canAccessOfficesMenu = () => hasCapability('can_access_offices_menu');

  // Slot capabilities
  const canViewSlots = () => hasCapability('can_view_slots');
  const canEditSlots = (siteId?: string) => {
    if (!hasCapability('can_edit_slots')) return false;
    if (isAdmin() || isSupport()) return true;
    if (isCsm() && siteId) {
      return me?.siteIds?.includes(siteId) ?? false;
    }
    return isCsm();
  };
  const canDeleteSlots = (siteId?: string) => {
    if (!hasCapability('can_delete_slots')) return false;
    if (isAdmin() || isSupport()) return true;
    if (isCsm() && siteId) {
      return me?.siteIds?.includes(siteId) ?? false;
    }
    return isCsm();
  };

  // Work schedule capabilities
  const canViewWorkSchedules = () => hasCapability('can_view_work_schedules');
  const canEditWorkSchedules = (siteId?: string) => {
    if (!hasCapability('can_edit_work_schedules')) return false;
    if (isAdmin() || isSupport()) return true;
    if (isCsm() && siteId) {
      return me?.siteIds?.includes(siteId) ?? false;
    }
    return isCsm();
  };
  const canCreateWorkSchedules = ({
    siteId,
    officeId,
  }: {
    siteId?: string;
    officeId?: string;
  } = {}) => {
    // can_create_own_work_schedules grants creation within offices the user can access
    if (hasCapability('can_create_own_work_schedules')) {
      return officeId ? canAccessOffice(officeId) : false;
    }
    return canEditWorkSchedules(siteId);
  };

  // Reservation capabilities
  const canViewReservations = () => hasCapability('can_view_reservations');
  const canEditReservations = (siteId?: string) => {
    if (!hasCapability('can_edit_reservations')) return false;
    if (isAdmin() || isSupport()) return true;
    if (isCsm() && siteId) {
      return me?.siteIds?.includes(siteId) ?? false;
    }
    return isCsm();
  };

  // Invitation capabilities
  const canManageInvitations = () => hasCapability('can_manage_invitations');

  // Contract capabilities
  const canViewAllContracts = () => hasCapability('can_view_all_contracts');
  const canViewOwnContracts = () => hasCapability('can_view_own_contracts');
  const canViewContracts = () => canViewAllContracts() || canViewOwnContracts();

  // Rhythm notes capabilities
  const canEditRhythmNotes = () => hasCapability('can_edit_rhythm_notes');

  // Financial fields edit capabilities (csm is excluded)
  const canEditDoctorFinancials = () =>
    hasCapability('can_edit_doctor_financials');
  const canEditOfficeFinancials = () =>
    hasCapability('can_edit_office_financials');

  // Office planning tools capabilities
  const canAccessOfficePlanningTools = () =>
    hasCapability('can_access_office_planning_tools');

  // Site access checks
  const canAccessSite = (siteId: string): boolean => {
    if (isAdmin() || isSupport()) return true;
    if (isCsm()) {
      return me?.siteIds?.includes(siteId) ?? false;
    }
    return false;
  };

  // Get site restrictions
  // Returns null for no restrictions (admin/support - all sites accessible)
  // Returns array of site IDs for CSM users (restricted to only these sites)
  // Returns empty array for substitute doctors (no site access)
  const getRestrictedSiteIds = (): string[] | null => {
    if (isAdmin() || isSupport()) return null; // No restrictions
    if (isCsm()) return me?.siteIds ?? [];
    return []; // Substitute doctors have no site access
  };

  // Filter sites by accessibility
  const filterAccessibleSites = (siteIds: string[]): string[] => {
    const restrictedIds = getRestrictedSiteIds();
    if (restrictedIds === null) return siteIds; // No restrictions
    return siteIds.filter((id) => restrictedIds.includes(id));
  };

  // Office access checks (for medical_assistant and attending_doctor roles)
  const canAccessOffice = (officeId: string): boolean => {
    if (isAdmin() || isSupport()) return true;
    // CSM access is handled via site membership (not direct office assignment)
    if (isCsm()) return true; // Office-level access is controlled by site
    if (isMedicalAssistant() || isOfficeAssistant() || isAttendingDoctor()) {
      return me?.officeIds?.includes(officeId) ?? false;
    }
    return false;
  };

  // Get office restrictions for medical_assistant and attending_doctor
  // Returns null for no office-level restrictions (admin/support/csm)
  // Returns array of office IDs for medical_assistant/attending_doctor (restricted to these offices)
  // Returns empty array for other roles (no direct office access)
  const getRestrictedOfficeIds = (): string[] | null => {
    if (isAdmin() || isSupport() || isCsm()) return null; // No office-level restrictions
    if (isMedicalAssistant() || isOfficeAssistant() || isAttendingDoctor())
      return me?.officeIds ?? [];
    return []; // Other roles have no office access
  };

  return {
    // Query state
    me,
    isLoading,
    isError,
    error,
    isFetching,

    // Role checks
    isAdmin,
    isSupport,
    isCsm,
    isSubstituteDoctor,
    isMedicalAssistant,
    isOfficeAssistant,
    isAttendingDoctor,
    hasAdminPrivileges,

    // Generic capability check
    hasCapability,

    // Feature capabilities
    canAccessDashboard,
    canAccessAccounting,
    canUseReferralProgram,
    canViewRecruitmentOffer,
    canViewProspects,
    canViewDocuments,
    canManageDocuments,
    canReadDocuments,
    canAccessAttendingAccounting,
    canImportAccountingFiles,
    canGenerateAccounting,
    canManageUsers,
    canManageAssistants,
    canViewAssistantsPlanning,
    canManageInvitations,

    // Resource capabilities (with optional site filtering)
    canViewSites,
    canEditSites,
    canCreateSites,
    canDeleteSites,
    canViewOffices,
    canEditOffices,
    canViewDoctors,
    canEditDoctors,
    canDeleteDoctors,
    canAccessDoctorSubstitutesMenu,
    canAccessDoctorAttendingsMenu,
    canAccessOfficesMenu,
    canViewSlots,
    canEditSlots,
    canDeleteSlots,
    canViewWorkSchedules,
    canEditWorkSchedules,
    canCreateWorkSchedules,
    canViewReservations,
    canEditReservations,

    // Site access
    canAccessSite,
    getRestrictedSiteIds,
    filterAccessibleSites,

    // Office access (for medical_assistant)
    canAccessOffice,
    getRestrictedOfficeIds,

    // Contract capabilities
    canViewAllContracts,
    canViewOwnContracts,
    canViewContracts,

    // Rhythm notes capabilities
    canEditRhythmNotes,

    // Financial fields edit capabilities
    canEditDoctorFinancials,
    canEditOfficeFinancials,

    // Office planning tools capabilities
    canAccessOfficePlanningTools,
  };
};
